import { Injectable, inject } from '@angular/core';
import { IRequestCallback, IRequestCallbackFunction, IRequestOptions, RequestService } from '@df/api/request/request.service';
import { Basket } from '@df/basket/basket.interfaces';
import { Product } from '@df/catalog/product/product';
import { App } from '@df/core/app';
import { Events } from '@df/core/service/event';
import { Status } from '@df/core/status';
import { TimeoutService } from '@df/ng/timeout.service';
import { BasketItem } from '@df/session/api/basket/basket-item';
import { Customer } from '@df/session/customer/customer.interfaces';
import { Nexus } from '@tomandco/nexus-api-types';
import compact from 'lodash-es/compact';
import find from 'lodash-es/find';
import isArray from 'lodash-es/isArray';
import isEmpty from 'lodash-es/isEmpty';
import reduce from 'lodash-es/reduce';
import { Subject, Subscription } from 'rxjs';

export interface IAddToBasketSuccessEventData {
    request: IAddToBasketRequestData;
    response: any;
}

export interface IAddToBasketRequestData {
    products: Nexus.Lib.Basket.BasketRequestData[];
}

export interface IRemoveFromBasketSuccessEventData {
    request: IRemoveFromBasketRequestData;
    response: any;
    items: BasketItem[];
}

export interface IRemoveFromBasketRequestData {
    ids: number[];
}

export interface IChangeQtyBasketItemRequestData {
    id: number;
    qty: number;
}

export interface IChangeQtyBasketItemSuccessEventData {
    request: IChangeQtyBasketItemRequestData;
    response: IResponseAddToBasket;
    previousQty?: number;
}

/**
 * Data interface for account/exists response
 */
export interface IResponseAddToBasket extends Customer.ILoadResponse {}

export interface IResponseRemoveFromBasket extends Customer.ILoadResponse {}

export interface IResponseChangeBasketItemRequest extends Customer.ILoadResponse {}

export interface IResponseFlushBasket extends Customer.ILoadResponse {}

export interface IPriceInfo {
    price: number;
    org_price?: number;
    is_from_price: boolean;
}

export interface IAddToBasketEventHandle {
    continue: boolean;
    requestOptions: IRequestOptions;
    proceed: () => Promise<any> | void;
}

export const BASKET_ADD_SUCCESS_EVENT = 'basket.add.success';

export const BASKET_ASS_SUCCESS_UPDATE_EVENT = 'basket.add.success.update';

@Injectable({
    providedIn: 'root'
})
export class BasketService {
    protected request = inject(RequestService);
    protected timeoutService = inject(TimeoutService);

    readonly addToBasketSuccessUpdateSubject = new Subject<IAddToBasketSuccessEventData>();

    readonly addToBasketSuccessSubject = new Subject<IAddToBasketSuccessEventData>();

    readonly addToBasketBeforeSubject = new Subject<{
        requestData: IAddToBasketRequestData;
        handle: IAddToBasketEventHandle;
    }>();

    readonly removeFromBasketSuccessSubject = new Subject<IRemoveFromBasketSuccessEventData>();

    readonly changeQtySuccessSubject = new Subject<IChangeQtyBasketItemSuccessEventData>();

    constructor(
        protected events: Events,
        protected app: App
    ) {}

    /**
     * Register observer
     * Observer function are called on basket update
     * @param  {(eventData: IAddToBasketSuccessEventData) => void} callback
     */
    subscribeToAddToBasketSuccess(...args): Subscription {
        return this.addToBasketSuccessSubject.subscribe(...args);
    }

    /**
     * Register observer
     * Observer function are called on basket update
     * @param  {({requestData: IAddToBasketRequestData, handle: IAddToBasketEventHandle}) => void} callback
     */
    subscribeToAddToBasketBefore(...args): Subscription {
        return this.addToBasketBeforeSubject.subscribe(...args);
    }

    /**
     * Register observer
     * Obserser function are called on basket item remove
     * @param {(eventData: IRemoveFromBasketSuccessEventData) => void} callback
     */
    subscribeToRemoveFromBasketSuccess(...args): Subscription {
        return this.removeFromBasketSuccessSubject.subscribe(...args);
    }

    subscribeToChangeQtySuccess(...args): Subscription {
        return this.changeQtySuccessSubject.subscribe(...args);
    }

    /**
     * Iterates request elements and checks for match
     * @param {Nexus.Lib.Basket.BasketRequestData) => boolean} callback
     */
    findInRequest(
        requests: undefined | Nexus.Lib.Basket.BasketRequestData | Nexus.Lib.Basket.BasketRequestData[],
        callback: (model: Product, request: Nexus.Lib.Basket.BasketRequestData) => boolean
    ): boolean {
        if (!requests) {
            return false;
        }
        return !!find(this.flattenRequest(requests), request => {
            if (!request.id) {
                return false;
            }
            const model = request.id ? this.app.resources.product.getExisting(request.id) : undefined;
            if (!model || !model.data) {
                return false;
            }
            return callback(model, request);
        });
    }

    /**
     * Flattens request grouping items by product
     *
     * NOTE THAT HERE WE CHECK INCLUDING EXISTING BASKET CONTENTS
     *
     * @param {Nexus.Lib.Basket.BasketRequestData | Nexus.Lib.Basket.BasketRequestData[]} requests
     */
    flattenRequest(
        requests: Nexus.Lib.Basket.BasketRequestData | Nexus.Lib.Basket.BasketRequestData[] | undefined
    ): Nexus.Lib.Basket.BasketRequestData[] {
        if (!requests) {
            return [];
        }
        if (!Array.isArray(requests)) {
            requests = [requests];
        }

        // in case we have more then one item lets group them to check their total qty
        requests = <Nexus.Lib.Basket.BasketRequestData[]>reduce(
            requests,
            (memo, request: Nexus.Lib.Basket.BasketRequestData) => {
                if (request.bundle_options && !isEmpty(request.bundle_options)) {
                    const bundleProduct = this.app.resources.product.getExisting(request.id);
                    if (bundleProduct) {
                        (bundleProduct.getBundleSelectedProducts(request.bundle_options) || []).forEach(item => {
                            if (item.product) {
                                push(item.product.id, item.qty);
                            }
                        });
                    }
                } else {
                    push(request.id, request.qty);
                }
                return memo;

                function push(id: number, qty = 1) {
                    let existing = find(memo, item => item.id === request.id);
                    if (!existing) {
                        existing = { id, qty: 0 };
                        memo.push(existing);
                    }
                    existing.qty += qty;
                }
            },
            <Nexus.Lib.Basket.BasketRequestData[]>[]
        );

        return requests;
    }

    /**
     * Retrieves max qty allowed to be added to basket
     *
     * setQty is used to identify if we have 2 items as set or 2 items separate - that determins how many sets you can buy
     *
     *
     * @param  {Nexus.Lib.Basket.BasketRequestData | Nexus.Lib.Basket.BasketRequestData[] | undefined} requests
     * @param  {number} setsQty request contains all items but you may want to add then as one piece or many,
     * how many pieces it is says setsQty
     * @return {number}
     */
    maxQty(
        requests: Nexus.Lib.Basket.BasketRequestData | Nexus.Lib.Basket.BasketRequestData[] | undefined,
        setsQty?: number,
        withBasket = true
    ): number {
        return this._maxQty(true, requests, setsQty, withBasket);
    }
    /**
     * Retrieves max in-stock qty (skipping backorders) allowed to be added to basket
     *
     * setQty is used to identify if we have 2 items as set or 2 items separate - that determins how many sets you can buy
     *
     * @param  {Nexus.Lib.Basket.BasketRequestData | Nexus.Lib.Basket.BasketRequestData[]| undefined} requests
     * @param  {number} setsQty request contains all items but you may want to add then as one piece or many,
     * how many pieces it is says setsQty
     * @return {number}
     */
    maxInQty(
        requests: Nexus.Lib.Basket.BasketRequestData | Nexus.Lib.Basket.BasketRequestData[] | undefined,
        setsQty?: number,
        withBasket = true
    ): number {
        return this._maxQty(false, requests, setsQty, withBasket);
    }

    private _maxQty(
        withBackorders: boolean,
        requests: Nexus.Lib.Basket.BasketRequestData | Nexus.Lib.Basket.BasketRequestData[] | undefined,
        setsQty?: number,
        withBasket = true
    ): number {
        return (
            this.minInRequests(requests, setsQty, (model: Product, request: Nexus.Lib.Basket.BasketRequestData) =>
                model.getStockQty(withBackorders, request, withBasket)
            ) || 0
        );
    }

    /**
     * Itereates all request and finds min value from callbacks
     * @param  {Nexus.Lib.Basket.BasketRequestData  |  Nexus.Lib.Basket.BasketRequestData[]} requests
     * @param  {Product, Nexus.Lib.Basket.BasketRequestData) => number }   callback  Callback retriving value for request
     * @return {number}
     */
    minInRequests(
        requests: Nexus.Lib.Basket.BasketRequestData | Nexus.Lib.Basket.BasketRequestData[] | undefined,
        setsQty: number = 1,
        callback: (model: Product, request: Nexus.Lib.Basket.BasketRequestData) => number
    ): number | undefined {
        return reduce(
            this.flattenRequest(requests),
            (memo: number | undefined, request: Nexus.Lib.Basket.BasketRequestData) => {
                const model = request.id ? this.app.resources.product.getExisting(request.id) : undefined;
                if (model) {
                    const qty = callback(model, request);
                    const qtyPerSet = request.qty / setsQty;
                    // that is expected to remain integer - request.qty should be multiplication of setsQty
                    const qtyLeft = Math.floor(qty / qtyPerSet);
                    if (memo === undefined || memo > qtyLeft) {
                        return qtyLeft;
                    }
                }
                return memo;
            },
            undefined
        );
    }

    /**
     * Checks if product is salable for given qty (in stock + backorders)
     */
    isSalable(requests?: Nexus.Lib.Basket.BasketRequestData | Nexus.Lib.Basket.BasketRequestData[]): boolean {
        return !this.findInRequest(requests, (model, request) => !model.isSalable(request.qty || 1, request));
    }

    /**
     * Checks if product is in stock (skipping backorders) for given qty
     */
    isInStock(requests?: Nexus.Lib.Basket.BasketRequestData | Nexus.Lib.Basket.BasketRequestData[]): boolean {
        return !this.findInRequest(requests, (model, request) => !model.isInStock(request.qty || 1, request));
    }

    /**
     * Calculates total price of given request(s)
     * @returns {IPriceInfo}
     */
    calcTotalPrices(requests: Nexus.Lib.Basket.BasketRequestData | Nexus.Lib.Basket.BasketRequestData[]): IPriceInfo {
        const priceInfo: IPriceInfo = {
            price: 0,
            is_from_price: false
        };

        let orgPrice = 0;

        // we can use find as each, just dont return true here
        this.findInRequest(requests, (model: Product, request: Nexus.Lib.Basket.BasketRequestData) => {
            priceInfo.price += request.qty * model.getPrice(request);

            orgPrice += request.qty * (model.getOrgPrice(request) || model.getPrice(request));
            if (model.data?.is_from_price) {
                priceInfo.is_from_price = true;
            }
            return false;
        });

        // skip org price if not higher then actual price (so we have same logic as for product)
        if (orgPrice - 0.01 > priceInfo.price) {
            priceInfo.org_price = orgPrice;
        }

        return priceInfo;
    }

    /**
     * Adds product to basket
     * You can use several types in request see param.
     * If number then will add product with that id and qty 1
     * If number then will add product with that string parsed to number as id and qty 1
     *
     * @param {addToBasketData | addToBasketData[] | number | string} addToBasketData
     */
    add(
        status: Status | undefined,
        addToBasketData: Nexus.Lib.Basket.BasketRequestData | Nexus.Lib.Basket.BasketRequestData[] | number | string | undefined,
        onSuccess?: IRequestCallbackFunction,
        onError?: IRequestCallback
    ): Promise<IResponseAddToBasket> | void {
        const products = this.normalizeAddToBasketData(addToBasketData);
        if (!products) {
            return;
        }
        const requestData: IAddToBasketRequestData = { products };
        const requestOptions: IRequestOptions = {};
        const handle: IAddToBasketEventHandle = {
            continue: true,
            requestOptions,
            proceed: (): Promise<any> | void => {
                return this.request.send(
                    status,
                    () => this.request.nexus.basket.add(requestData),
                    result => {
                        if (onSuccess) {
                            const successResult = onSuccess(result);
                            if (successResult) {
                                result = successResult;
                            }
                        }
                        const eventData: IAddToBasketSuccessEventData = {
                            request: requestData,
                            response: result
                        };
                        this.addToBasketSuccessSubject.next(eventData);
                        this.events.broadcast(BASKET_ADD_SUCCESS_EVENT, eventData);
                        this.timeoutService.setTimeout(() => {
                            this.addToBasketSuccessUpdateSubject.next(eventData);
                            this.events.broadcast(BASKET_ASS_SUCCESS_UPDATE_EVENT, eventData);
                        });
                        return result;
                    },
                    onError,
                    requestOptions
                );
            }
        };

        this.addToBasketBeforeSubject.next({ requestData, handle });

        // send add to basket request
        if (handle.continue) {
            return handle.proceed();
        }
    }

    /**
     * Probes BE for totals
     * You can use several types in request see param.
     * If number then will add product with that id and qty 1
     * If number then will add product with that string parsed to number as id and qty 1
     *
     * @param {addToBasketData | addToBasketData[] | number | string} addToBasketData
     */
    probe(
        status: Status | undefined,
        addToBasketData: Nexus.Lib.Basket.BasketRequestData | Nexus.Lib.Basket.BasketRequestData[] | number | string | undefined,
        onSuccess?: IRequestCallbackFunction,
        onError?: IRequestCallback
    ): void | Promise<any> {
        const requestData = {
            products: this.normalizeAddToBasketData(addToBasketData)
        };

        if (requestData.products && requestData.products.length) {
            // send add to basket request
            return this.request.send(status, { path: Basket.ENDPOINTS.probe, data: requestData }, onSuccess, onError);
        } else if (onSuccess) {
            onSuccess({ price: 0 });
        }
    }

    /**
     * Normalizes any allowed input data to standard interface
     * @param {addToBasketData | addToBasketData[] | number | string} addToBasketData
     * @return {addToBasketData} Normalized request data
     */
    normalizeAddToBasketData(
        inputData: Nexus.Lib.Basket.BasketRequestData | Nexus.Lib.Basket.BasketRequestData[] | number | string | undefined
    ): Nexus.Lib.Basket.BasketRequestData[] | undefined {
        switch (typeof inputData) {
            case 'number':
                return [
                    {
                        id: <number>inputData,
                        qty: 1
                    }
                ];
            case 'string':
                return this.normalizeAddToBasketData(parseInt(<string>inputData, 10));
            case 'object':
                if ((<any>inputData).length !== undefined) {
                    return <Nexus.Lib.Basket.BasketRequestData[]>inputData;
                }
                return [<Nexus.Lib.Basket.BasketRequestData>inputData];
        }

        return undefined;
    }

    /**
     * Saves buyRequests of items
     */
    saveRequests(status: Status, items: BasketItem | BasketItem[]): Promise<IResponseChangeBasketItemRequest> | undefined {
        const itemsArray: BasketItem[] = (Array.isArray(items) ? items : [items]).filter(i => i.requestChanged);

        if (!itemsArray.length) {
            return undefined;
        }

        return this.request.send(status, () =>
            this.request.nexus.basket.changeRequest({
                items: itemsArray.map(item => ({ id: item.id, request: item.data.request }))
            })
        );
    }

    changeRequest(
        status: Status | undefined,
        itemId: number,
        request: Nexus.Lib.Basket.BasketRequestData,
        onSuccess?: IRequestCallback,
        onError?: IRequestCallback
    ): Promise<IResponseChangeBasketItemRequest> {
        return this.request.send(
            status,
            () => this.request.nexus.basket.changeRequest({ items: [{ id: itemId, request }] }),
            onSuccess,
            onError
        );
    }

    /**
     *
     * @param {Status | undefined} status
     * @param {number | number[]} id
     * @param {IRequestCallback} onSuccess
     * @param {IRequestCallback} onError
     * @param {boolean} checkout Do the same as remove but additionaly we receive checkoutPatch in response needed to update payment,
     * shipping methods
     */
    remove(
        status: Status | undefined,
        id: number | number[],
        onSuccess?: IRequestCallback,
        onError?: IRequestCallback,
        checkout = false
    ): Promise<IResponseRemoveFromBasket> {
        const data: IRemoveFromBasketRequestData = {
            ids: isArray(id) ? id : [id]
        };
        const items = compact(data.ids.map(basketItemId => this.app.customer.basket.byId(basketItemId)));

        const callback = response => {
            const eventData: IRemoveFromBasketSuccessEventData = {
                request: data,
                response,
                items
            };
            this.removeFromBasketSuccessSubject.next(eventData);
        };

        if (isArray(onSuccess)) {
            onSuccess.push(callback);
        } else {
            onSuccess = compact([callback, onSuccess]);
        }

        return this.request.send(
            status,
            checkout ? { path: Basket.ENDPOINTS.checkoutRemove, data } : () => this.request.nexus.basket.remove(data),
            onSuccess,
            onError
        );
    }

    flush(status: Status | undefined, onSuccess?: IRequestCallback, onError?: IRequestCallback): Promise<Nexus.Api.Basket.Flush.Response> {
        return this.request.send(status, () => this.request.nexus.basket.flush(), onSuccess, onError);
    }

    setQty(
        status: Status,
        data: IChangeQtyBasketItemRequestData,
        onSuccess?: IRequestCallback,
        onError?: IRequestCallback
    ): Promise<IResponseAddToBasket> {
        const onSuccessCallback = (response: IResponseAddToBasket) => {
            const eventData: IChangeQtyBasketItemSuccessEventData = {
                request: data,
                response,
                previousQty: this.app.customer.basket.items.find(item => item.id === data.id)?.qty
            };
            this.changeQtySuccessSubject.next(eventData);
        };
        if (isArray(onSuccess)) {
            onSuccess.push(onSuccessCallback);
        } else {
            onSuccess = compact([onSuccess, onSuccessCallback]);
        }
        return this.request.send(status, () => this.request.nexus.basket.qty(data), onSuccess, onError);
    }
}
